import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

import { apiClient } from '@/lib/api/client';
import { publicApiClient } from '@/lib/api/publicClient';
import { useTenantStore } from '@/stores/tenantStore';

import { usePublicRun } from '../publicRun';

const esignStatusSchema = z.object({
  /** Whether a signature request has been raised for this step at all. */
  issued: z.boolean(),
  /** SignCare's document status, or 'NotSent' / 'Pending' before the first callback. */
  status: z.string(),
  signerCount: z.number().int(),
  signedCount: z.number().int(),
  /** Whether the person on this screen has signed — the signal the frame waits on. */
  applicantSigned: z.boolean(),
  /** The applicant's own signing page; null once they've signed. */
  applicantSignLink: z.string().nullable(),
  rejectReason: z.string().nullable(),
});

export type EsignStatus = z.infer<typeof esignStatusSchema>;

/**
 * Signing progress for an eSign step.
 *
 * A pure read of what SignCare's webhook last recorded — no provider call, nothing
 * billed — so this is safe to run on mount and to poll. Running on MOUNT is what lets
 * a refreshed applicant land back in the signing frame rather than on a button that
 * would re-POST a billed call.
 *
 * The interval tightens while the applicant is actually signing (the webhook can land
 * at any second and we want the frame swapped out promptly), and relaxes once they are
 * only waiting on other people, who take minutes to days.
 */
export function useEsignStatus(sessionId: string | undefined, stepId: string) {
  const tenantId = useTenantStore((s) => s.tenantId);
  const publicRun = usePublicRun();

  return useQuery({
    queryKey: ['esign-status', publicRun?.token ?? sessionId, stepId],
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data?.issued) return false; // nothing raised yet — nothing to watch
      if (!data.applicantSigned) return 1500; // mid-signature: swap the frame out promptly
      if (data.signedCount >= data.signerCount) return false; // everyone is done
      return 15_000; // waiting on other people, who take minutes at best
    },
    refetchIntervalInBackground: false,
    queryFn: async () => {
      const path = publicRun
        ? `api/v1/public/sessions/${publicRun.token}/steps/${stepId}/esign-status`
        : `api/v1/t/${tenantId}/sessions/${sessionId}/steps/${stepId}/esign-status`;
      const client = publicRun ? publicApiClient : apiClient;

      return esignStatusSchema.parse(await client.get(path).json());
    },
  });
}
